import { getConfig } from 'config';
import { getProductIdsOptions } from 'helpers/getProductIdsOptions';

import { DOCUMENT_TYPE } from 'api/documents/documents.model';
import DatePicker from 'components/Form/DatePicker';
import Select from 'components/Form/Select';
import { useCertificatesQuery } from 'hooks/useCertificatesQuery';
import {
  ADDITIONAL_DETAILS,
  CHEMICAL_COMPOSITION_TEST_RESULTS_DETAILS,
  DEALER_DETAILS,
  NESTED_DETAILS,
  PARTICLE_SIZE_DISTRIBUTION_DETAILS,
  PHYSICAL_PROPERTIES_DETAILS,
  TEST_RESULTS_DETAILS,
} from 'models/certificate.model';
import { COMPANY } from 'models/company.model';
import { CATEGORY_TYPE } from 'models/documents.model';

export const useConfig = (category: CATEGORY_TYPE) => {
  const { data } = useCertificatesQuery({
    documentType: DOCUMENT_TYPE.PUBLIC,
    categoryName: (() => {
      switch (category) {
        case CATEGORY_TYPE.CHEMISTRY_TEST_RESULTS:
          return CATEGORY_TYPE.CHEMISTRY;
        case CATEGORY_TYPE.BATTERY_CELLS_TEST_RESULTS:
        case CATEGORY_TYPE.BATTERY_CELLS_SHIPPING_CONFIRMATION:
        case CATEGORY_TYPE.BATTERY_CELLS_RECEIVING_CONFIRMATION:
          return CATEGORY_TYPE.BATTERY_CELLS;
        case CATEGORY_TYPE.BATTERY_PACKS_TEST_RESULTS:
        case CATEGORY_TYPE.CAR_DEALERS_SHIPPING_CONFIRMATION:
          return CATEGORY_TYPE.BATTERY_PACKS;
      }
    })(),
  });

  const productIdsOptions = getProductIdsOptions(data?.rows);

  const getRcipientDetails = (company: COMPANY) => {
    const recipientDetails = getConfig(company);

    return {
      recipient: recipientDetails.name,
      receiverPublisherId: recipientDetails.publisherId,
    };
  };

  switch (category) {
    case CATEGORY_TYPE.CHEMISTRY_TEST_RESULTS:
      return {
        ...getRcipientDetails(COMPANY.AUTHORITIES),
        formFields: [
          {
            id: ADDITIONAL_DETAILS.RECIPIENT,
            disabled: true,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.CHEMISTRY_MARKET_NAME,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.CHEMISTRY_BATCH_NUMBER,
            required: true,
            component: Select,
            options: productIdsOptions,
          },
          {
            id: ADDITIONAL_DETAILS.TEST_DATE,
            component: DatePicker,
            required: true,
          },
          {
            id: 'specification',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.CHEMICAL_COMPOSITION_TEST_RESULTS,
            label: ADDITIONAL_DETAILS.CHEMICAL_COMPOSITION,
            nested: Object.values(CHEMICAL_COMPOSITION_TEST_RESULTS_DETAILS).map((value) => ({
              id: value,
            })),
          },
          {
            id: ADDITIONAL_DETAILS.PHYSICAL_PROPERTIES_TEST_RESULTS,
            label: ADDITIONAL_DETAILS.PHYSICAL_PROPERTIES,
            nested: Object.values(PHYSICAL_PROPERTIES_DETAILS).map((value) => ({
              id: value,
            })),
          },
          {
            id: ADDITIONAL_DETAILS.PARTICLE_SIZE_DISTRIBUTION_TEST_RESULTS,
            label: ADDITIONAL_DETAILS.PARTICLE_SIZE_DISTRIBUTION,
            nested: Object.values(PARTICLE_SIZE_DISTRIBUTION_DETAILS).map((value) => ({
              id: value,
            })),
          },
        ],
      };
    case CATEGORY_TYPE.BATTERY_CELLS_TEST_RESULTS:
      return {
        ...getRcipientDetails(COMPANY.AUTHORITIES),
        formFields: [
          {
            id: ADDITIONAL_DETAILS.RECIPIENT,
            disabled: true,
            required: true,
          },
          {
            id: NESTED_DETAILS.BATTERY_CELL_MODEL,
            required: true,
          },
          {
            id: NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER,
            required: true,
            component: Select,
            options: productIdsOptions,
          },
          {
            id: ADDITIONAL_DETAILS.TEST_DATE,
            component: DatePicker,
            required: true,
          },
          {
            id: 'specification',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.TEST_RESULTS,
            nested: [
              TEST_RESULTS_DETAILS.NOMINAL_CAPACITY,
              TEST_RESULTS_DETAILS.AVERAGE_NOMINAL_CAPACITY,
              TEST_RESULTS_DETAILS.NOMINAL_VOLTAGE,
              TEST_RESULTS_DETAILS.VOLTAGE,
              TEST_RESULTS_DETAILS.ENERGY,
            ].map((value) => ({
              id: value,
            })),
          },
        ],
      };
    case CATEGORY_TYPE.BATTERY_PACKS_TEST_RESULTS:
      return {
        ...getRcipientDetails(COMPANY.AUTHORITIES),
        formFields: [
          {
            id: ADDITIONAL_DETAILS.RECIPIENT,
            disabled: true,
            required: true,
          },
          {
            id: NESTED_DETAILS.EVMODEL,
            required: true,
          },
          {
            id: NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER,
            required: true,
            component: Select,
            options: productIdsOptions,
          },
          {
            id: ADDITIONAL_DETAILS.TEST_DATE,
            component: DatePicker,
            required: true,
          },
          {
            id: 'specification',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.TEST_RESULTS,
            nested: [
              TEST_RESULTS_DETAILS.HEALTH_TEST,
              TEST_RESULTS_DETAILS.TOTAL_CAPACITY,
              TEST_RESULTS_DETAILS.USABLE_CAPACITY,
              TEST_RESULTS_DETAILS.WEIGHT,
              TEST_RESULTS_DETAILS.GRAVIMETRIC_ENERGY_DENSITY,
            ].map((value) => ({
              id: value,
            })),
          },
        ],
      };
    case CATEGORY_TYPE.BATTERY_CELLS_SHIPPING_CONFIRMATION:
      return {
        ...getRcipientDetails(COMPANY.BATTERY_PACKS),
        formFields: [
          {
            id: ADDITIONAL_DETAILS.RECIPIENT,
            disabled: true,
            required: true,
          },
          {
            id: NESTED_DETAILS.BATTERY_CELL_MODEL,
            required: true,
          },
          {
            id: NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER,
            required: true,
            component: Select,
            options: productIdsOptions,
          },
          {
            id: ADDITIONAL_DETAILS.SHIPPING_DATE,
            component: DatePicker,
            required: true,
          },
        ],
      };
    case CATEGORY_TYPE.BATTERY_CELLS_RECEIVING_CONFIRMATION:
      return {
        ...getRcipientDetails(COMPANY.BATTERY_CELLS),
        formFields: [
          {
            id: ADDITIONAL_DETAILS.RECIPIENT,
            disabled: true,
            required: true,
          },
          {
            id: NESTED_DETAILS.BATTERY_CELL_MODEL,
            required: true,
          },
          {
            id: NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER,
            required: true,
            component: Select,
            options: productIdsOptions,
          },
          {
            id: ADDITIONAL_DETAILS.RECEIVING_DATE,
            component: DatePicker,
            required: true,
          },
        ],
      };
    case CATEGORY_TYPE.CAR_DEALERS_SHIPPING_CONFIRMATION:
      return {
        recipient: 'Car Dealers',
        receiverPublisherId: process.env.REACT_APP_CAR_DEALERS_PUBLISHER_ID,
        formFields: [
          {
            id: ADDITIONAL_DETAILS.RECIPIENT,
            disabled: true,
            required: true,
          },
          {
            id: NESTED_DETAILS.EVMODEL,
            required: true,
          },
          {
            id: NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER,
            required: true,
            component: Select,
            options: productIdsOptions,
          },
          {
            id: ADDITIONAL_DETAILS.SHIPPING_DATE,
            component: DatePicker,
            required: true,
          },
          {
            id: NESTED_DETAILS.SALE_DATE,
            component: DatePicker,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.DEALER,
            nested: Object.values(DEALER_DETAILS).map((value) => ({
              id: value,
            })),
          },
        ],
      };
  }
};
